---
title: "We counted the weekend builds: 162 repos, median 3 stars, 58 already stale"
seoTitle: "Why build your own meeting tool? 162 repos counted"
description: "In a 90-day window to 2026-08-15, 162 new repos matched meeting capture and agent memory. Median 3 stars, 113 at five or fewer, 58 with no push in a month."
date: "2026-08-15"
lastUpdated: "2026-08-15"
author: "The Chief team"
labels: ["Research"]
tags: ["build vs buy", "meeting transcription", "local first", "whisper", "developer tools"]
coverStat:
  value: "162"
  label: "repos in 90 days, median 3 stars"
cluster: "meeting-context"
draft: true
---

> **Disclosure.** We build a product in this category, so we have an interest in how this
> question gets answered. That's exactly why the numbers below are counted rather than asserted,
> the method is stated, and every quote carries its author, platform and date.

If you've thought about building your own meeting capture instead of paying for one, you're in
a large group. We wanted to know how large, and what happens to those projects afterwards. So
we counted.

The honest summary: building is common, cheap to start, and decays fast. None of that makes it
the wrong call. It does mean the decision deserves better inputs than a Show HN title.

> **Key Takeaways**
> - In the 90 days to 2026-08-15, 162 new GitHub repos matched meeting transcription, local
>   notetaking or agent memory. Median: 3 stars.
> - 113 of the 162 sat at five stars or fewer, 30 at zero, and 58 had already gone a full month
>   without a push — inside the same window they were created in.
> - Across 118 dated items on five platforms we found 109 build artifacts and 9 product
>   evaluations. On GitHub and dev.to, the evaluation count was zero.
> - The people building say why, plainly, and export lock-in is the trigger more often than price.

## What did we count, and how?

{/* [ORIGINAL DATA] */}
We swept five platforms for items published between 2026-05-17 and 2026-08-15, then hand-checked
every date before including anything. Titles proved unreliable — several posts carrying "2026"
turned out to predate the window by a year — so dates came from platform APIs rather than page
scraping.

The GitHub layer is a repo-creation query against meeting transcription, local notetaking, and
agent memory and context. It returned 162 repositories created inside the window. Star counts
and last-push timestamps were read at the same time, on 2026-08-15.

The five-platform layer is narrower and hand-picked: 118 dated items. Each was classified as
either a **build artifact** (someone shipping a thing they made) or a **product evaluation**
(someone comparing products). That classification is a judgment call,
and it's the one number here we'd expect a careful reader to argue with.

Two limits worth stating up front. Reddit isn't in this data at all — it was unreachable from
the environment that ran the sweep, and r/selfhosted and r/LocalLLaMA are exactly where the
abandonment conversation would sit. And a star count measures attention, not usefulness. A tool
with three stars can still be the right tool for the person who wrote it.

## What does the star distribution actually show?

Concentration at the bottom. Of 162 repos created in the window, 113 had five stars or fewer and
30 had none at all, measured on 2026-08-15. The median was 3.

<figure>
<svg viewBox="0 0 560 300" role="img" aria-label="Donut chart of star counts across 162 repositories: 30 with zero stars, 83 with one to five stars, 49 with six or more.">
  <g transform="translate(150,150)">
    <circle r="85" fill="none" stroke="currentColor" stroke-opacity="0.12" stroke-width="42"/>
    <g transform="rotate(-90)">
      <circle r="85" fill="none" stroke="#C2543E" stroke-width="42"
              stroke-dasharray="98.9 435.2" stroke-dashoffset="0"/>
      <circle r="85" fill="none" stroke="#E0A33E" stroke-width="42"
              stroke-dasharray="273.6 260.5" stroke-dashoffset="-98.9"/>
      <circle r="85" fill="none" stroke="#4F86D6" stroke-width="42"
              stroke-dasharray="161.5 372.6" stroke-dashoffset="-372.5"/>
    </g>
    <text x="0" y="-4" text-anchor="middle" font-size="30" font-weight="700"
          fill="currentColor">162</text>
    <text x="0" y="18" text-anchor="middle" font-size="12" fill="currentColor"
          fill-opacity="0.7">repos</text>
  </g>
  <g font-size="13" fill="currentColor">
    <rect x="300" y="86" width="13" height="13" fill="#C2543E"/>
    <text x="322" y="97">0 stars — 30 repos (19%)</text>
    <rect x="300" y="126" width="13" height="13" fill="#E0A33E"/>
    <text x="322" y="137">1–5 stars — 83 repos (51%)</text>
    <rect x="300" y="166" width="13" height="13" fill="#4F86D6"/>
    <text x="322" y="177">6+ stars — 49 repos (30%)</text>
    <text x="300" y="214" fill-opacity="0.7" font-size="12">Median: 3 stars</text>
  </g>
</svg>
<figcaption>Star counts for 162 repositories created 2026-05-17 to 2026-08-15, read 2026-08-15.
Original analysis.</figcaption>
</figure>

Seventy percent of the window's output sat at five stars or fewer. That's the shape of a
category where the marginal cost of starting has fallen close to zero, and the cost of
finishing hasn't moved at all.

## Is the build side growing or decaying?

Both, at the same time. Creation held steady through the window — 23 repos in May, 62 in June,
62 in July, and 15 in the first half of August. Meanwhile 58 of the 162 had already gone a full
month without a push, inside the same window in which they were created.

<figure>
<svg viewBox="0 0 560 300" role="img" aria-label="Line chart of repository creation by month: 23 in May, 62 in June, 62 in July, 15 in the first half of August.">
  <g stroke="currentColor" stroke-opacity="0.15">
    <line x1="90" y1="250" x2="530" y2="250"/>
    <line x1="90" y1="193" x2="530" y2="193"/>
    <line x1="90" y1="136" x2="530" y2="136"/>
    <line x1="90" y1="79" x2="530" y2="79"/>
  </g>
  <g font-size="11" fill="currentColor" fill-opacity="0.65" text-anchor="end">
    <text x="80" y="254">0</text><text x="80" y="197">20</text>
    <text x="80" y="140">40</text><text x="80" y="83">60</text>
  </g>
  <polyline points="120,184 255,73 385,73 505,207" fill="none" stroke="#4F86D6" stroke-width="3"
            stroke-linejoin="round"/>
  <g fill="#4F86D6">
    <circle cx="120" cy="184" r="5"/><circle cx="255" cy="73" r="5"/>
    <circle cx="385" cy="73" r="5"/><circle cx="505" cy="207" r="5"/>
  </g>
  <g font-size="13" font-weight="700" fill="currentColor" text-anchor="middle">
    <text x="120" y="172">23</text><text x="255" y="61">62</text>
    <text x="385" y="61">62</text><text x="505" y="195">15</text>
  </g>
  <g font-size="12" fill="currentColor" fill-opacity="0.75" text-anchor="middle">
    <text x="120" y="272">May</text><text x="255" y="272">Jun</text>
    <text x="385" y="272">Jul</text><text x="505" y="272">Aug 1–15</text>
  </g>
  <text x="505" y="288" font-size="10" fill="currentColor" fill-opacity="0.55"
        text-anchor="middle">half month</text>
</svg>
<figcaption>New repositories by month of creation, 2026-05-17 to 2026-08-15. August covers 15 days
only. Original analysis.</figcaption>
</figure>

August looks like a collapse and isn't — it's half a month. Read the first three columns as the
trend and the fourth as a partial count.

{/* [UNIQUE INSIGHT] */}
The decay figure is the one that should change a decision. A month of silence inside a 90-day
window means a third of these projects were abandoned before they were old enough to be
maintained. Not abandoned after a hard year of upkeep. Abandoned during the build.

The written side shows the same shape. Across the window, 403 dev.to posts matched the topic
keywords, with a median of 0 reactions and 342 of the 403 carrying no comments at all. The build
guide is written far more often than it's read.

## Where does the writing actually happen?

Overwhelmingly on the build side, and the split differs sharply by platform. Across 118 dated,
hand-checked items we found 109 build artifacts and 9 product evaluations.

<figure>
<svg viewBox="0 0 560 340" role="img" aria-label="Grouped bar chart comparing build artifacts and product evaluations by platform: Hacker News 45 and 5, GitHub 31 and 0, dev.to 23 and 0, YouTube 8 and 3, Medium 2 and 1.">
  <g font-size="12" fill="currentColor">
    <rect x="105" y="14" width="12" height="12" fill="#4F86D6"/>
    <text x="123" y="24">Build artifacts</text>
    <rect x="245" y="14" width="12" height="12" fill="#E0A33E"/>
    <text x="263" y="24">Product evaluations</text>
  </g>
  <g font-size="13" fill="currentColor" text-anchor="end">
    <text x="96" y="76">Hacker News</text><text x="96" y="131">GitHub</text>
    <text x="96" y="186">dev.to</text><text x="96" y="241">YouTube</text>
    <text x="96" y="296">Medium</text>
  </g>
  <g fill="#4F86D6">
    <rect x="105" y="55" width="425" height="15"/><rect x="105" y="110" width="293" height="15"/>
    <rect x="105" y="165" width="217" height="15"/><rect x="105" y="220" width="76" height="15"/>
    <rect x="105" y="275" width="19" height="15"/>
  </g>
  <g fill="#E0A33E">
    <rect x="105" y="73" width="47" height="15"/><rect x="105" y="183" width="0" height="15"/>
    <rect x="105" y="238" width="28" height="15"/><rect x="105" y="293" width="9" height="15"/>
  </g>
  <g font-size="12" font-weight="700" fill="currentColor">
    <text x="536" y="67">45</text><text x="404" y="122">31</text>
    <text x="328" y="177">23</text><text x="187" y="232">8</text><text x="130" y="287">2</text>
  </g>
  <g font-size="12" fill="currentColor" fill-opacity="0.8">
    <text x="158" y="85">5</text><text x="111" y="140">0</text>
    <text x="111" y="195">0</text><text x="139" y="250">3</text><text x="120" y="305">1</text>
  </g>
</svg>
<figcaption>Build artifacts versus product evaluations across 118 dated items, 2026-05-17 to
2026-08-15. Original analysis.</figcaption>
</figure>

On GitHub and dev.to the evaluation count isn't low. It's zero. Nobody in that window used
either platform to compare products in this category. The comparison content that does exist on
dev.to is vendor-authored and falls outside the window.

That asymmetry matters when you're researching a decision. If you search this category, you'll
mostly find people describing what they built, because that's what gets written. You won't find
many people describing what happened six months later.

## Why do people build, when the products exist?

Export lock-in, more often than price. The clearest case in the window came from someone whose
existing workflow broke: *"Granola are cracking down on their auth and I can no longer pull my
meeting notes out and put them in Obsidian (my plugin is gonna have to be deprecated)"*
(tomelliot, Hacker News, 2026-07-16).

The principle underneath it got stated in one line, by someone who says they aren't a developer
at all: *"If I can't read it without you, it's not really mine"* (dthcst, Hacker News,
2026-07-29). The same comment opens *"I got tired of my stuff living on someone else's server,
so I ended up building my own local memory thing. Not a developer at all, just stubborn."*

Some build because the products fail on fundamentals rather than on features. *"I did a survey
of many and they all (that I could find) skip the fundamentals"* (blopker, Hacker News,
2026-06-14), in a comment that goes on to list crash recovery among the gaps.

And some build because switching tools threatens something they've already assembled. One
commenter listed the wiki, feature plans, retrospectives and skills built up around their
existing setup. The barrier to trying anything new was *"will I maintain my edge"*
(starcalleryisss, Hacker News, 2026-07-07).

Price appears too, and it's specific rather than general: *"I can't stand monthly rent forever"*
(stevenicr, Hacker News, 2026-07-28).

{/* INTERNAL-LINK: capture is solved, retrieval is not → spoke on the post-capture gap */}

## What does the hostility in these threads target?

Builders, mostly, which is not what you'd expect. The sharpest replies in this corpus land on
the person who shipped the weekend project rather than on vendors or subscriptions. The trigger
is the framing rather than the code.

On a Show HN presenting an open-source alternative built in a weekend: *"This is by far the least
interesting/worst kind of project emerging lately. 'I didn't want to pay trivial money for a
product, so I paid nontrivial money to have an AI make a version that's worse in every way that
matters' Who cares?"* (vitally3643, Hacker News, 2026-07-27).

In the same thread, from a different angle: *"I will never understand that thinking. You didn't
like that a service you need asks to be paid, so you paid an LLM to create a free competitor
that you now advertise online. Do you not see the race to the bottom you're contributing to? $24
doesn't sound outrageous at all."* (dgellow, Hacker News, 2026-07-27).

{/* [UNIQUE INSIGHT] */}
We're quoting these because they're the market's own reaction, not because we share them. A
vendor repeating this as an argument for buying would be borrowing hostility aimed at exactly
the people most worth listening to. The useful signal is narrower: what draws fire is the
*weekend* framing rather than the building.

Builders themselves are often the most realistic voices in the room. One maintainer of a local
transcription app wrote: *"The next gen of models will prob one shot my app anyway"*
(marshalla, Hacker News, 2026-07-29).

## Does anyone switch, in either direction?

Yes, both ways, and neither is common enough in this window to call a trend. The fullest arc
came in a single sentence: *"I found MacWhisper to be great. I built and used my own solution
for a couple of years before settling on MacWhisper"* (schappim, Hacker News, 2026-07-16).

Note the timescale. Two years of running his own, then a switch. That reads as a long working
solution being retired for a better one, rather than a weekend experiment ending in
disappointment.

The reverse direction is at least as well attested. Several commenters in the window reported
surveying existing products and finding the survey itself was what pushed them to start
building. Nobody in this corpus wrote up their own abandonment, which is a gap in the evidence
rather than a sign it doesn't happen — abandonment doesn't produce a post.

## How should you actually decide?

Use the counts as a prior, not a verdict. Three questions separate the builds that survive from
the 58 that went quiet.

1. **Is capture the whole job, or the first step?** Local transcription is a solved weekend
   problem. Getting back into a year of it is not, and that's where homemade systems and bought
   ones both tend to stop.
2. **What breaks when you stop maintaining it?** The failure modes practitioners name —
   diarization, mic and system audio as separate lanes, crash recovery — are ongoing work, not
   one-time work.
3. **What's the actual trigger?** If it's export lock-in, building solves it permanently. If
   it's the monthly cost, check the number against your own hourly rate before starting.

If you're building because you want to own the file, you're in the strongest position in this
data. That's the motive with the clearest statement and the best survival odds, because the goal
is reachable in a weekend and stays reached.

{/* INTERNAL-LINK: where AI prototypes stop → spoke on the hardening wall */}

## Frequently Asked Questions

<details>
<summary>How many people are building their own meeting tools?</summary>

More than the star counts suggest. In the 90 days to 2026-08-15 we counted 162 new GitHub repos
matching meeting transcription, local notetaking or agent memory, plus 403 dev.to posts on the
same topics. Attention is concentrated at the bottom: median 3 stars, median 0 reactions.

</details>

<details>
<summary>Do these projects get maintained?</summary>

Often not. 58 of the 162 repos had gone a full month without a push inside the same 90-day
window they were created in, measured 2026-08-15. That's abandonment during the build rather
than after a long maintenance burden.

</details>

<details>
<summary>Is it cheaper to build than to buy?</summary>

Depends on what you count. The stated triggers in the window were export lock-in and
subscription fatigue — *"I can't stand monthly rent forever"* (stevenicr, Hacker News,
2026-07-28). The recurring counter-argument is that a weekend of your time plus model costs
isn't free either.

</details>

<details>
<summary>What breaks in homemade meeting capture?</summary>

Practitioners name the same set repeatedly: diarization, mic and system audio bleeding into one
lane, crash recovery, and hallucinated filler in silence. One builder surveying existing tools
said they *"all (that I could find) skip the fundamentals"* (blopker, Hacker News, 2026-06-14).

</details>

<details>
<summary>Why is there so little product comparison content?</summary>

Because almost nobody writes it. Across 118 dated items we found 9 product evaluations against
109 build artifacts, and on GitHub and dev.to the evaluation count was zero. The category's
public writing is dominated by people documenting what they made.

</details>

## What this changes

The build-versus-buy argument in this category usually gets fought with anecdotes. Now there are
counts: 162 repos, median 3 stars, 113 at five or fewer, 58 stale inside the window, 109 build
artifacts against 9 evaluations.

None of that says don't build. It says the distribution is heavily weighted toward projects that
stop. The writing you'll find while researching is skewed toward beginnings rather than
outcomes. And the motive you start with predicts a lot.

We'll re-run this count and publish the delta. The interesting number is how many of the 162 are
still getting pushes in six months.

{/* INTERNAL-LINK: the meeting already contains the work → cluster hub */}

---

## Sources

All Hacker News items retrieved and date-verified via the Algolia API on 2026-08-15.

- tomelliot, "Ask HN: Best meeting transcription daemon for macOS?", Hacker News, 2026-07-16.
  https://news.ycombinator.com/item?id=48936123
- schappim, comment on the same thread, Hacker News, 2026-07-16.
  https://news.ycombinator.com/item?id=48941504
- marshalla, comment on the same thread, Hacker News, 2026-07-29.
  https://news.ycombinator.com/item?id=49101488
- dthcst, comment on "Show HN: Echologue – the private AI voice journal I built for myself",
  Hacker News, 2026-07-29. https://news.ycombinator.com/item?id=49096901
- blopker, comment on "Show HN: Trace – Offline Mac meeting transcripts you can flag mid-call",
  Hacker News, 2026-06-14. https://news.ycombinator.com/item?id=48533220
- vitally3643, comment on "Show HN: Descript wanted $24/mo, I built an open-source alternative in
  a weekend", Hacker News, 2026-07-27. https://news.ycombinator.com/item?id=49073386
- dgellow, comment on the same thread, Hacker News, 2026-07-27.
  https://news.ycombinator.com/item?id=49072256
- stevenicr, comment on the same thread, Hacker News, 2026-07-28.
  https://news.ycombinator.com/item?id=49079604
- starcalleryisss, comment on "Show HN: Rowboat – Open-source, local-first alternative to Claude
  Desktop", Hacker News, 2026-07-07. https://news.ycombinator.com/item?id=48824783
- Original analysis, 2026-08-15. GitHub repo-creation query for meeting transcription, local
  notetaking, and agent memory and context, restricted to repos created 2026-05-17 to
  2026-08-15: 162 results, with stars and last-push timestamps read on 2026-08-15. Separately,
  118 dated items across Hacker News, GitHub, dev.to, YouTube and Medium, hand-classified as
  build artifact or product evaluation. Reddit is absent from the dataset and was not sampled.
